import fp from 'lodash/fp';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { components } from 'react-select';

import { Select2, Text, useMemoDeps, usePrevious } from 'Modules/ui';
import { useAction } from 'Modules/core/redux';
import { useTranslation } from 'Modules/core/i18n';

import { paginationActions } from '../../state/pagination/actions';
import { paginationStatuses } from '../../state/pagination/statuses';
import { getDomainTypeFromPath } from '../../state/pagination/utils';
import styles from './index.module.scss';
import { usePagination, useStorage } from '../../utils';


const ValueContainer = ({ children, ...props }) => (
    <components.SingleValue title={children} {...props}>
        <span title={props.data.label}>{children}</span>
    </components.SingleValue>
);

export const useSelectPagination = (path, max, defaultParameters) => {
    const request = useAction(
        () => paginationActions.request(
            path,
            { start: 0, stop: max },
        ),
        [path, max],
    );

    const setParameters = useAction(paginationActions.setParameters);
    const parameters = usePagination(path, 'parameters');

    useEffect(
        () => {
            setParameters(path, defaultParameters);
        },
        [path, defaultParameters, setParameters],
    );

    const queue = usePagination(path, 'queue');
    const isLoading = useMemo(
        () => fp.get(`[0:${max}]`, queue) === paginationStatuses.PENDING,
        [max, queue],
    );
    const isLoaded = useMemo(
        () => queue &&
            queue[`0:${max}`] &&
            queue[`0:${max}`] !== paginationStatuses.PENDING,
        [max, queue],
    );

    const filters = useMemoDeps(fp.get('filters'), [parameters]);
    useEffect(
        () => {
            if (filters && !isLoading && !isLoaded)
                request();
        },
        [filters, isLoading, isLoaded, request],
    );

    const elements = usePagination(path, 'elements');
    const total = usePagination(path, 'total') || 0;

    return {
        isCreate: !!parameters,
        isLoaded,
        parameters,
        elements,
        total,
        setParameters,
    };
};

export const useOptions = ({ path, value, mapValueToOptions, mapItemToOption }) => {
    const [options, setOptions] = useState([]);

    const domain = useMemoDeps(getDomainTypeFromPath, [path]);
    const storage = useStorage(domain);

    const mapElementsToOptions = useMemo(
        () => fp.pipe(
            fp.filter(element => !!element),
            fp.map(element => mapItemToOption(storage[element])),
            elements => setOptions(value && mapValueToOptions
                ? mapValueToOptions(value, storage, elements)
                : elements),
        ),
        [storage, value, mapValueToOptions, mapItemToOption],
    );

    return {
        options,
        mapToOptions: mapElementsToOptions,
    };
};

const defaultMapSearchToFilters = fp.constant({ filters: {} });

/** @type {React.FC<any>} */
export const PaginationSelect = React.memo(({
    path,
    disabled = false,
    value,
    maxResults,
    mapSearchToFilters = defaultMapSearchToFilters,
    mapItemToOption,
    mapValueToOptions,
    overflowKey,
    placeholder,
    loadingMessage,
    components: customComponents,
    customMenuList: CustomMenuList = components.MenuList,
    Component = Select2,
    ...props
}) => {
    const defaultParameters = useMemo(
        () => mapSearchToFilters('', value),
        [value, mapSearchToFilters],
    );
    const {
        isCreate,
        isLoaded,
        parameters,
        elements,
        total,
        setParameters,
    } = useSelectPagination(
        path,
        maxResults,
        defaultParameters,
    );

    const [loading, needLoading] = useState(true);
    const { options, mapToOptions } = useOptions({
        path,
        value,
        mapValueToOptions,
        mapItemToOption,
    });

    const prevIsLoaded = usePrevious(isLoaded);
    useEffect(
        () => {
            if (isLoaded && !prevIsLoaded) {
                mapToOptions(elements);
                needLoading(false);
            }
        },
        [elements, prevIsLoaded, isLoaded, mapToOptions],
    );

    const onInputChange = useCallback(
        inputValue => {
            const newParameters = mapSearchToFilters(inputValue, value);
            if (!isCreate || !fp.isEqual(newParameters, parameters)) {
                needLoading(true);
                setParameters(
                    path,
                    newParameters,
                );
            }
        },
        [path, value, parameters, isCreate, mapSearchToFilters, setParameters],
    );

    const { t } = useTranslation();
    const MenuList = useCallback(
        ({ children, ...props }) => (
            <CustomMenuList {...props}>
                {children}
                {total !== 0 && total > maxResults && (
                    <Text component='div' textType='info' textCompact className={styles.overflow}>
                        {t(overflowKey, { count: total - maxResults })}
                    </Text>
                )}
            </CustomMenuList>
        ),
        [CustomMenuList, maxResults, total, overflowKey, t],
    );

    return (
        <Component
            key={path}
            variant='field'
            value={value}
            options={options}
            onInputChange={onInputChange}
            components={{
                MenuList,
                SingleValue: ValueContainer,
                ...customComponents,
            }}
            placeholder={placeholder}
            disabled={disabled}
            loadingMessage={loadingMessage}
            isLoading={loading}
            filterOption={fp.T}
            {...props}
        />
    );
});
